import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDiscord, Discord } from './StaffDiscordContext';
import toast from 'react-hot-toast';
import GridItem from 'components/Grid/GridItem';
import FormControl from '@mui/material/FormControl';
import CustomInput from 'components/CustomInput/CustomInput.js';
import Button from 'components/CustomButtons/Button.js';
import { Checkbox, FormControlLabel } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { addTeamMember, removeMemberFromTeam } from './StaffDiscordApi';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

interface StaffDiscordsFormProps {
  selectedDiscord?: Discord | null;
  onUpdate?: () => void;
  selectionCount: number;
  onClose: () => void;
}

interface Member {
  id: string;
  name: string;
}

interface TeamMember {
  id: string;
  name?: string;
}

interface FormDataState {
  name: string;
  description: string;
  inviteLink: string;
  rank: string;
  logoUpload: string | File;
  published: boolean;
  modMembers: Member[];
  slug: string;
}

// Add a new interface for the API response
interface TeamApiResponse {
  id: string;
  slug: string;
  name: string;
  description: string;
  logo: string;
  logo_url: string;
  discord_id: string;
  invite_link: string;
  published: boolean;
  rank: number;
  created_at: string;
  updated_at: string;
  members: (string | number)[];
}

const StaffDiscordForm: React.FC<StaffDiscordsFormProps> = ({
  selectedDiscord,
  onUpdate,
  selectionCount,
  onClose,
}) => {
  const { t } = useTranslation();
  const { createDiscord, users, fetchTeamById } = useDiscord();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormDataState>({
    name: '',
    description: '',
    inviteLink: '',
    rank: '0',
    logoUpload: '',
    published: false,
    modMembers: [],
    slug: '',
  });
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<Member | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalMemberIds, setOriginalMemberIds] = useState<string[]>([]);

  const handleReset = useCallback(() => {
    // Reset form data to initial empty state
    setFormData({
      name: '',
      description: '',
      inviteLink: '',
      rank: '0',
      logoUpload: '',
      published: false,
      modMembers: [], // Clear members array
      slug: '',
    });

    // Reset all state variables
    setIsEditing(false);
    setEditingId(null);
    setSelectedUser(null);
    setOriginalMemberIds([]);

    // Reset file input using ref
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Reset Autocomplete component
    const autocompleteElement = document.querySelector(
      '.MuiAutocomplete-clearIndicator'
    ) as HTMLElement;
    if (autocompleteElement) {
      autocompleteElement.click();
    }

    // Call onUpdate callback if provided to notify parent component
    onUpdate?.();
  }, [onUpdate]);

  useEffect(() => {
    if (selectedDiscord) {
      setEditingId(selectedDiscord.id);
      setIsEditing(true);
      setFormData({
        name: selectedDiscord.name || '',
        description: selectedDiscord.description || '',
        inviteLink: selectedDiscord.inviteLink || '',
        rank: String(selectedDiscord.rank || 0),
        logoUpload: selectedDiscord.logoUpload || '',
        published: selectedDiscord.published || false,
        modMembers: (selectedDiscord.modMembers || []).map((member: string | TeamMember) => {
          if (typeof member === 'string') {
            return { id: member, name: '' };
          }
          return { id: member.id, name: member.name || '' };
        }),
        slug: selectedDiscord.slug || '',
      });
    } else {
      setEditingId(null);
      setIsEditing(false);
      handleReset();
    }
  }, [handleReset, selectedDiscord]);

  useEffect(() => {
    if (editingId) {
      const fetchTeamData = async () => {
        try {
          // Fetch the team data from the API
          const response = (await fetchTeamById(editingId)) as unknown as TeamApiResponse;

          // Store original member IDs to avoid redundant requests later
          if (response.members && Array.isArray(response.members)) {
            const memberIds = response.members.map((id) => String(id));
            setOriginalMemberIds(memberIds);
          }

          // Map member IDs to full user objects
          let memberObjects: Member[] = [];
          if (response.members && Array.isArray(response.members) && users) {
            memberObjects = response.members.map((memberId: number | string) => {
              // Find the full user object from the users array
              const user = users.find((u) => u.id === memberId || u.id === String(memberId));
              return {
                id: String(memberId),
                name: user ? user.name : `User ${memberId}`,
              };
            });
          }

          // Convert API response to our Discord interface format
          const teamData: Partial<Discord> = {
            id: response.id,
            name: response.name || '',
            description: response.description || '',
            inviteLink: response.invite_link || '',
            rank: response.rank || 0,
            logoUpload: response.logo_url || '',
            published: response.published || false,
            slug: response.slug || '',
            modMembers: memberObjects.map((m) => m.id),
          };

          setFormData({
            name: teamData.name || '',
            description: teamData.description || '',
            inviteLink: teamData.inviteLink || '',
            rank: String(teamData.rank || 0),
            logoUpload: teamData.logoUpload || '',
            published: teamData.published || false,
            modMembers: memberObjects,
            slug: teamData.slug || '',
          });
        } catch (error) {
          console.error('Error fetching team data:', error);
        }
      };
      fetchTeamData();
    }
  }, [editingId, fetchTeamById, users]);

  const handleChange = (e: { target: { name: string; value: boolean | string } }) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, logoUpload: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      if (formData.name) formDataToSend.append('name', formData.name);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.inviteLink) formDataToSend.append('inviteLink', formData.inviteLink);
      if (formData.slug) formDataToSend.append('slug', formData.slug);
      if (formData.logoUpload) {
        if (formData.logoUpload instanceof File) {
          formDataToSend.append('logoUpload', formData.logoUpload);
        } else {
          formDataToSend.append('logoUpload', formData.logoUpload);
        }
      }
      formDataToSend.append('published', formData.published.toString());
      // Ensure rank is a number
      const rankValue = parseInt(formData.rank, 10);
      formDataToSend.append('rank', rankValue.toString());

      // Add members to FormData
      if (formData.modMembers && formData.modMembers.length > 0) {
        formData.modMembers.forEach((member) => {
          formDataToSend.append('modMembers[]', member.id);
        });
      } else {
        // Ensure we don't send null for members
        formDataToSend.append('modMembers[]', '');
      }

      if (isEditing && editingId) {
        // When updating, explicitly convert rank to number
        const rankNumber = parseInt(formData.rank, 10);

        // Prepare FormData for update
        const updateData = new FormData();
        updateData.append('name', formData.name || '');
        updateData.append('description', formData.description || '');
        updateData.append('inviteLink', formData.inviteLink || '');
        updateData.append('published', formData.published.toString());
        updateData.append('rank', rankNumber.toString());
        updateData.append('slug', formData.slug || '');

        // Handle logoUpload properly
        if (formData.logoUpload) {
          if (formData.logoUpload instanceof File) {
            updateData.append('logoUpload', formData.logoUpload);
          } else if (typeof formData.logoUpload === 'string' && formData.logoUpload.trim() !== '') {
            updateData.append('logoUpload', formData.logoUpload);
          }
        }

        // Add members to FormData
        if (formData.modMembers && formData.modMembers.length > 0) {
          formData.modMembers.forEach((member) => {
            updateData.append('modMembers[]', member.id);
          });
        } else {
          // Ensure we don't send null for members
          updateData.append('modMembers[]', '');
        }

        try {
          const sessionToken = localStorage.getItem('session_token') || '';

          const response = await fetch(`http://77.21.112.94:8001/api/team/${editingId}`, {
            method: 'PUT',
            headers: {
              'session-token': sessionToken,
            },
            body: updateData,
          });

          if (!response.ok) {
            console.error('Error response:', response.statusText);
            const errorText = await response.text();
            console.error('Error details:', errorText);
            throw new Error(`Failed to update Discord: ${response.statusText}`);
          }

          const responseText = await response.text();

          try {
            if (responseText) {
              JSON.parse(responseText);
            }
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            console.error('Raw response text:', responseText);
            throw new Error('Error parsing server response');
          }

          // Invalidate the teams query to refresh the table
          queryClient.invalidateQueries({ queryKey: ['teams'] });

          // Add only new members to the team (if not already in the team)
          for (const member of formData.modMembers) {
            const isMemberExisting = originalMemberIds && originalMemberIds.includes(member.id);
            if (!isMemberExisting) {
              try {
                await addTeamMember(editingId, member.id);
                toast.success(t('discords.form.success.memberAdded', { name: member.name }));
              } catch (error) {
                toast.error(t('discords.form.error.addMember', { name: member.name }));
              }
            }
          }

          // Show success message, reset form, and close
          toast.success(t('discords.form.success.updated'));
          handleReset(); // Full form reset
          onClose();
        } catch (error) {
          console.error('Error updating Discord:', error);
          throw error;
        }
      } else {
        // Create the Discord team
        try {
          const createdTeam = await createDiscord(formDataToSend);

          if (createdTeam && createdTeam.id) {
            // Add each member to the newly created team
            for (const member of formData.modMembers) {
              try {
                await addTeamMember(createdTeam.id, member.id);
                toast.success(t('discords.form.success.memberAdded', { name: member.name }));
              } catch (error) {
                toast.error(t('discords.form.error.addMember', { name: member.name }));
              }
            }

            // Show success message, reset form, and close
            toast.success(t('discords.form.success.created'));
            handleReset(); // Full form reset
            onClose();
          }
        } catch (createError) {
          console.error('Error creating team:', createError);
          throw createError;
        }
      }
    } catch (err) {
      console.error('Form submission error:', err);
      const errorKey = isEditing ? 'discords.form.error.update' : 'discords.form.error.create';
      toast.error(t(errorKey));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle removing a single member
  const handleRemoveMember = async (userId: string, userName: string) => {
    if (isEditing && editingId) {
      try {
        // Call API to remove member
        await removeMemberFromTeam(editingId, userId);
        toast.success(t('discords.form.success.memberRemoved', { name: userName }));

        // Update local state after successful API call
        setFormData((prev) => ({
          ...prev,
          modMembers: prev.modMembers.filter((m) => m.id !== userId),
        }));
      } catch (error) {
        console.error('Error removing member:', error);
        toast.error(t('discords.form.error.memberRemoveFailed', { name: userName }));
      }
    } else {
      // Just update local state if not editing
      setFormData((prev) => ({
        ...prev,
        modMembers: prev.modMembers.filter((m) => m.id !== userId),
      }));
    }
  };

  // Function to handle bulk deletion of members
  const handleDeleteSelectedMembers = async () => {
    if (isEditing && editingId && selectedMemberIds.length > 0) {
      try {
        // Call API to remove each selected member one by one
        let successCount = 0;
        let errorCount = 0;

        for (const memberId of selectedMemberIds) {
          try {
            await removeMemberFromTeam(editingId, memberId);
            successCount++;
          } catch (error) {
            errorCount++;
          }
        }

        // Show appropriate toast messages
        if (successCount > 0) {
          toast.success(t('discords.form.success.membersRemoved', { count: successCount }));
        }

        if (errorCount > 0) {
          toast.error(t('discords.form.error.someRemovalsFailed', { count: errorCount }));
        }

        // Update local state to remove all members that were attempted to be removed
        setFormData((prev) => ({
          ...prev,
          modMembers: prev.modMembers.filter((m) => !selectedMemberIds.includes(m.id)),
        }));

        // Clear selection
        setSelectedMemberIds([]);
      } catch (error) {
        console.error('Error removing members:', error);
        toast.error(t('discords.form.error.membersRemoveFailed'));
      }
    } else if (!isEditing) {
      // Just update local state if not editing
      setFormData((prev) => ({
        ...prev,
        modMembers: prev.modMembers.filter((m) => !selectedMemberIds.includes(m.id)),
      }));
      setSelectedMemberIds([]);
    }
  };

  return (
    <GridItem xs={12} sm={12} md={12}>
      <FormControl id="discordsForm" fullWidth={true}>
        <div style={{ margin: '20px 0' }}>
          <Autocomplete
            id="member-search"
            value={selectedUser}
            onChange={(event, newValue) => {
              setSelectedUser(newValue);
              if (newValue && !formData.modMembers.some((member) => member.id === newValue.id)) {
                setFormData((prev) => ({
                  ...prev,
                  modMembers: [...prev.modMembers, newValue],
                }));
              }
            }}
            options={users || []}
            getOptionLabel={(option) => option?.id + ': ' + option?.name || ''}
            renderInput={(params) => (
              <TextField {...params} label={t('discords.searchUsers')} fullWidth />
            )}
            fullWidth
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            width: '100%',
          }}
        >
          <Button
            color="primary"
            disabled={selectedMemberIds.length === 0}
            onClick={handleDeleteSelectedMembers}
            fullWidth
          >
            {t('discords.deleteSelectedUsers')}
          </Button>
        </div>
        <div style={{ height: 400, width: '100%', marginBottom: '20px' }}>
          <DataGrid
            rows={formData.modMembers}
            columns={[
              { field: 'id', headerName: 'ID', flex: 1 },
              { field: 'name', headerName: t('discords.name'), flex: 2 },
              {
                field: 'actions',
                type: 'actions',
                headerName: t('discords.table.actions'),
                flex: 1,
                getActions: (params) => [
                  <GridActionsCellItem
                    key="delete"
                    icon={<DeleteIcon />}
                    label={t('discords.table.delete')}
                    onClick={() => {
                      if (isEditing && editingId) {
                        // Call the remove member function
                        handleRemoveMember(params.row.id, params.row.name);
                      } else {
                        // Just update local state if not editing
                        setFormData((prev) => ({
                          ...prev,
                          modMembers: prev.modMembers.filter((m) => m.id !== params.row.id),
                        }));
                      }
                    }}
                    color="error"
                  />,
                ],
              },
            ]}
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => {
              // Just update the selection state, don't modify modMembers
              setSelectedMemberIds(newSelection.map((id) => id.toString()));
            }}
            rowSelectionModel={selectedMemberIds}
            disableRowSelectionOnClick
          />
        </div>
        <CustomInput
          labelText={t('discords.slug')}
          id="slug"
          value={formData.slug}
          onChange={(event) =>
            handleChange({
              target: {
                name: 'slug',
                value: event.target.value,
              },
            })
          }
          inputProps={{
            type: 'text',
          }}
          required
        />
        <CustomInput
          labelText={t('discords.nodeName')}
          id="name"
          value={formData.name}
          onChange={(event) =>
            handleChange({
              target: {
                name: 'name',
                value: event.target.value,
              },
            })
          }
          inputProps={{
            type: 'text',
          }}
          required
        />
        <CustomInput
          labelText={t('discords.description')}
          id="description"
          value={formData.description}
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            multiline: true,
            rows: 5,
          }}
          onChange={(event) =>
            handleChange({
              target: {
                name: 'description',
                value: event.target.value,
              },
            })
          }
        />
        <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.published}
              onChange={(event) =>
                handleChange({
                  target: {
                    name: 'published',
                    value: event.target.checked,
                  },
                })
              }
              name="published"
              color="primary"
            />
          }
          label={t('discords.published')}
          sx={{
            color: (theme) => theme.palette.text.primary,
          }}
        />
        <CustomInput
          labelText={t('discords.rank')}
          id="rank"
          value={formData.rank}
          onChange={(event) =>
            handleChange({
              target: {
                name: 'rank',
                value: event.target.value,
              },
            })
          }
          inputProps={{
            type: 'number',
          }}
          required
        />
        <CustomInput
          labelText={t('discords.inviteLink')}
          id="inviteLink"
          value={formData.inviteLink}
          onChange={(event) =>
            handleChange({
              target: {
                name: 'inviteLink',
                value: event.target.value,
              },
            })
          }
          inputProps={{
            type: 'text',
          }}
          required
        />
        <GridItem xs={12} sm={12} md={12}>
          <Button
            color="primary"
            type="submit"
            disabled={isSubmitting || isEditing}
            fullWidth={true}
            form="discordsForm"
            onClick={handleSubmit}
          >
            {t('discords.form.create')}
          </Button>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <Button
            color="primary"
            type="submit"
            disabled={isSubmitting || !isEditing || selectionCount !== 1}
            fullWidth={true}
            form="discordsForm"
            onClick={handleSubmit}
          >
            {t('discords.form.update')}
          </Button>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <Button
            color="outlined"
            type="button"
            disabled={isSubmitting}
            fullWidth={true}
            onClick={handleReset}
          >
            {t('discords.form.cancel')}
          </Button>
        </GridItem>
      </FormControl>
    </GridItem>
  );
};

export default StaffDiscordForm;
